import { useEffect, useMemo, useState } from "react";
import { Video, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const pad = (n) => String(n).padStart(2, "0");

export const Livestream = () => {
  const target = useMemo(() => {
    const now = new Date();
    let t = new Date(now.getFullYear(), 11, 15, 10, 0, 0);
    if (t.getTime() <= now.getTime()) t = new Date(now.getFullYear() + 1, 11, 15, 10, 0, 0);
    return t;
  }, []);

  const [left, setLeft] = useState(target.getTime() - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(target.getTime() - Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  const s = Math.max(Math.floor(left / 1000), 0);
  const units = [
    { v: pad(Math.floor(s / 86400)), l: "Days" },
    { v: pad(Math.floor((s % 86400) / 3600)), l: "Hours" },
    { v: pad(Math.floor((s % 3600) / 60)), l: "Minutes" },
    { v: pad(s % 60), l: "Seconds" },
  ];

  return (
    <section id="livestream" className="relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="livestream-section">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/2 top-1/2 h-[70vh] w-[70vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.09),transparent_65%)]" />
      </div>

      <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <div className="glass-gold relative overflow-hidden rounded-3xl p-8 sm:p-14 gold-glow-soft" data-testid="livestream-card">
            <Video className="absolute -right-6 -top-6 h-40 w-40 text-gold/5" aria-hidden="true" />
            <div className="grid items-center gap-10 md:grid-cols-2">
              <div>
                <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Watching from afar</p>
                <h2 className="mt-4 font-serif text-3xl font-medium text-ivory sm:text-5xl">
                  The ceremony,
                  <span className="text-gold-foil italic"> streamed live</span>
                </h2>
                <p className="mt-5 text-sm font-light leading-relaxed text-mist sm:text-base">
                  FIU gives each graduate just four guest tickets, so not everyone can be in the
                  room. On <span className="text-gold-light">Tuesday, December 15 at 10:00 AM</span>,
                  we're streaming the ceremony live — tick the box in your RSVP and we'll email
                  you the link before the big day.
                </p>
                <button
                  data-testid="livestream-request-cta"
                  onClick={() => scrollToId("rsvp")}
                  className="mt-8 flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy-deep transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_36px_rgba(212,175,55,0.35)] active:scale-95"
                >
                  <Send size={14} /> Request the private link
                </button>
              </div>

              <div className="text-center md:text-right" data-testid="countdown-timer">
                <p className="text-[10px] uppercase tracking-[0.35em] text-mist">Countdown to commencement</p>
                <div className="mt-6 flex justify-center gap-3 md:justify-end">
                  {units.map((u) => (
                    <div key={u.l} className="w-[68px] rounded-2xl border border-gold/25 bg-navy/70 px-2 py-4 sm:w-[76px]">
                      <div className="text-gold-foil font-serif text-3xl font-semibold tabular-nums sm:text-4xl">{u.v}</div>
                      <div className="mt-1 text-[9px] uppercase tracking-[0.25em] text-mist">{u.l}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-5 font-script text-3xl text-gold-light/90">see you at the ceremony</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
